import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from './Alert';
import { Button } from './Button';

function Fallback({ onReload }) {
  const { t } = useTranslation();
  return (
    <Alert tone="danger" title={t('ui.loadFailed.title')}
           action={<Button variant="secondary" size="sm" onClick={onReload}>{t('ui.loadFailed.reload')}</Button>}>
      <p>{t('ui.loadFailed.text')}</p>
    </Alert>
  );
}

/**
 * Keeps a part of the page that failed to render (a lazy chunk that did not
 * download — offline, or a release replaced it) from blanking the whole
 * app: an alert with "Reload" instead. `onReload` defaults to reloading the
 * page (a new release's chunks come with the new index.html).
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV !== 'test') console.error(error);
  }

  render() {
    if (this.state.failed) {
      const reload = this.props.onReload || (() => window.location.reload());
      return this.props.fallback || <Fallback onReload={reload} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
